import type { FC, HTMLAttributes, ReactNode } from 'react'
import { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useEvent } from 'react-use'
import type {
  DateRange,
  GenerationModel,
  ListGenerationModelsQueryVariables,
} from 'src/services/apollo/generated/graphql'
import { cx } from 'src/utils/cx'
import inView from 'src/utils/inView'
import { track } from 'src/utils/tracker'
import Empty from '../../Empty'
import LoadingPage from '../../LoadingPage'
import type { GenerationModelFeedResult } from './useGenerationModelFeed'

export interface ListState {
  showNsfw: boolean
  tag: string
  keyword: string
  types?: string[]
  cursor?: string | undefined
  order?: string
  authorId?: string
  timeRange?: DateRange
}

/** Query/filter args — kept exported so callers can build args externally */
export interface ModelInfiniteScrollProps {
  tag?: string
  tackId?: string
  keyword?: string
  order?: string
  type?: string
  challenge?: number
  authorId?: string
  pageSize?: number
  extraArgs?: ListGenerationModelsQueryVariables
  timeRange?: DateRange
  scene?: string
}

export interface ModelInfiniteScrollContentProps {
  scene?: string
  models: GenerationModel[]
  loadMore: () => void
  mutateModels: () => void
  onModelClick: (model: GenerationModel) => any
  loading: boolean
  /**
   * For displaying skeleton
   */
  pageSize?: number
}

export interface BaseModelListInfiniteScrollProps {
  feedResult: GenerationModelFeedResult
  scene?: string
  pageSize?: number
  renderLoading?: () => ReactNode
  renderContent: (props: ModelInfiniteScrollContentProps) => ReactNode
  renderEmpty?: () => ReactNode
  containerProps?: HTMLAttributes<HTMLDivElement>
  /**
   * Modify grid elements after model elements have been assembled.
   *
   * @usage Inserting ad units to ModelGrid
   */
  modifyModelElements?: (
    elements: (JSX.Element | null)[],
    opts?: {
      gridItemType: string
    },
  ) => (JSX.Element | null)[]
}

const BaseModelListInfiniteScroll: FC<BaseModelListInfiniteScrollProps> = ({
  feedResult,
  pageSize = 24,
  renderEmpty,
  renderLoading = () => <LoadingPage />,
  renderContent,
  containerProps,
  scene = 'market',
}) => {
  const { t } = useTranslation(['artwork'])
  const { models, loading, loadingMore, hasNext, loadMore, mutateModels } =
    feedResult

  const autoLoadRef = useRef<HTMLDivElement>(null)

  useEvent(
    'scroll',
    useCallback(() => {
      if (
        autoLoadRef.current &&
        inView(autoLoadRef.current, { offset: -500 })
      ) {
        loadMore()
      }
    }, [loadMore]),
    typeof window !== 'undefined' ? window : null,
    { passive: true },
  )

  const onModelClick = useCallback(
    (model: GenerationModel) => {
      track('model_click', {
        scene,
        type: model.type,
      })
    },
    [scene],
  )

  const content = loading ? (
    renderLoading()
  ) : models.length === 0 ? (
    renderEmpty ? (
      renderEmpty()
    ) : (
      <Empty description={t('artwork:search-results.empty-without-keyword')} />
    )
  ) : (
    renderContent({
      models,
      loadMore,
      onModelClick,
      loading: loadingMore,
      pageSize,
      mutateModels,
    })
  )

  return (
    <>
      <div
        {...containerProps}
        className={cx(
          'w-full flex justify-center mb-8 min-h-screen',
          containerProps?.className,
        )}
      >
        {content}
      </div>
      <div ref={autoLoadRef} className="w-full flex justify-center mb-8">
        {hasNext ? <LoadingPage fullHeight /> : null}
      </div>
    </>
  )
}

export default BaseModelListInfiniteScroll
